import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaChartLine,
  FaSpinner,
  FaTrophy,
  FaCheckCircle,
  FaTimesCircle,
  FaUserGraduate,
  FaDownload,
  FaShare,
  FaBook,
  FaCalendarAlt,
  FaUniversity,
  FaStar,
  FaCrown,
  FaMedal,
  FaEye,
  FaArrowLeft,
  FaRocket,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import { useFooter } from "../context/FooterContext";

const CompareResults = () => {
  const [usns, setUsns] = useState(["", ""]);
  const [semester, setSemester] = useState("all");
  const [examType, setExamType] = useState("Regular");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compared, setCompared] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { setShowFooter } = useFooter();

  // Hide footer
  React.useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  React.useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const semesters = ["all", 1, 2, 3, 4, 5, 6, 7, 8];

  const handleUsnChange = (index, value) => {
    const newUsns = [...usns];
    newUsns[index] = value.toUpperCase();
    setUsns(newUsns);
  };

  const handleAddUsn = () => {
    if (usns.length < 5) {
      setUsns([...usns, ""]);
    } else {
      toast.error("Maximum 5 USNs can be compared");
    }
  };

  const handleRemoveUsn = (index) => {
    if (usns.length > 2) {
      const newUsns = usns.filter((_, i) => i !== index);
      setUsns(newUsns);
    } else {
      toast.error("At least 2 USNs required for comparison");
    }
  };

  const handleCompare = async () => {
    const validUsns = usns.filter((usn) => usn.trim().length > 0);
    if (validUsns.length < 2) {
      toast.error("Please enter at least 2 valid USNs");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/api/results/compare-live", {
        usns: validUsns,
        semester: semester === "all" ? null : semester,
        examType,
      });
      setResults(response.data.results);
      setCompared(true);
      toast.success("Comparison completed!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to compare results");
    } finally {
      setLoading(false);
    }
  };

  const downloadComparison = async () => {
    const element = document.getElementById("comparison-results");
    if (element) {
      try {
        const canvas = await html2canvas(element);
        const link = document.createElement("a");
        link.download = "result-comparison.png";
        link.href = canvas.toDataURL();
        link.click();
        toast.success("Downloaded as image!");
      } catch (error) {
        toast.error("Failed to download");
      }
    }
  };

  const shareComparison = () => {
    const text = results
      .map((r) => `${r.name} (${r.usn}): ${r.totalMarks} marks`)
      .join("\n");
    if (navigator.share) {
      navigator.share({ title: "Result Comparison", text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    }
  };

  const sortedResults = [...results].sort(
    (a, b) => (b.totalMarks || 0) - (a.totalMarks || 0),
  );

  const getRankBadge = (index) => {
    if (index === 0)
      return {
        bg: "linear-gradient(135deg, #f59e0b, #d97706)",
        color: "white",
        icon: <FaCrown />,
        label: "1st",
      };
    if (index === 1)
      return {
        bg: "linear-gradient(135deg, #94a3b8, #64748b)",
        color: "white",
        icon: <FaMedal />,
        label: "2nd",
      };
    if (index === 2)
      return {
        bg: "linear-gradient(135deg, #cd7f32, #b87333)",
        color: "white",
        icon: <FaMedal />,
        label: "3rd",
      };
    return {
      bg: isDarkMode ? "rgba(139,92,246,0.2)" : "#e2e8f0",
      color: getTextColor(),
      icon: <FaTrophy />,
      label: `${index + 1}th`,
    };
  };

  const toggleExpand = (usn) => {
    setExpandedStudent(expandedStudent === usn ? null : usn);
  };

  // Styling helpers
  const getBackgroundColor = () => (isDarkMode ? "#0a0a0f" : "#f5f7fb");
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Confetti animation for top performer
  const [showConfetti, setShowConfetti] = useState(false);

  React.useEffect(() => {
    if (compared && results.length > 0) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [compared, results]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 400, damping: 25 },
    },
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -30, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
    hover: {
      scale: 1.01,
      backgroundColor: isDarkMode
        ? "rgba(139,92,246,0.1)"
        : "rgba(139,92,246,0.05)",
      transition: { duration: 0.2 },
    },
  };

  const expandVariants = {
    hidden: { opacity: 0, height: 0, y: -20 },
    visible: {
      opacity: 1,
      height: "auto",
      y: 0,
      transition: { duration: 0.4, ease: "easeInOut" },
    },
    exit: {
      opacity: 0,
      height: 0,
      y: -20,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    transition: { duration: 0.5, repeat: Infinity, repeatDelay: 1 },
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: { duration: 3, repeat: Infinity, ease: "easeInOut" },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: getBackgroundColor(),
        padding: isMobile ? "20px 16px" : "40px 20px",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* Confetti Effect - Simple celebration */}
      <AnimatePresence>
        {showConfetti && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              zIndex: 1000,
              overflow: "hidden",
            }}
          >
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: -50,
                  rotate: 0,
                }}
                animate={{
                  y: window.innerHeight + 50,
                  rotate: Math.random() * 360,
                }}
                transition={{
                  duration: Math.random() * 2 + 1,
                  delay: Math.random() * 0.5,
                  ease: "linear",
                }}
                style={{
                  position: "absolute",
                  width: Math.random() * 10 + 5,
                  height: Math.random() * 10 + 5,
                  background: `hsl(${Math.random() * 360}, 70%, 50%)`,
                  borderRadius: "50%",
                }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header with Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{ marginBottom: "2rem" }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#8b5cf6",
              textDecoration: "none",
              marginBottom: "1rem",
              fontSize: "0.9rem",
              padding: "0.5rem 1rem",
              borderRadius: "10px",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.1)";
              e.target.style.transform = "translateX(-5px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)";
              e.target.style.transform = "translateX(0)";
            }}
          >
            <FaArrowLeft /> Back to Dashboard
          </Link>

          <motion.div
            animate={floatingAnimation}
            style={{
              background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
              borderRadius: "28px",
              padding: "2rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
              transition={{ duration: 4, repeat: Infinity }}
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background:
                  "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
                borderRadius: "50%",
              }}
            />
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "0.5rem",
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "50px",
                    height: "50px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HiOutlineSparkles size={24} color="white" />
                </motion.div>
                <h1
                  style={{
                    fontSize: isMobile ? "1.8rem" : "2rem",
                    fontWeight: "bold",
                    color: "white",
                    margin: 0,
                  }}
                >
                  Compare <span style={{ color: "#8b5cf6" }}>Results</span>
                </h1>
              </div>
              <p style={{ color: "rgba(255,255,255,0.9)", marginLeft: "3rem" }}>
                Compare academic performance across multiple students
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Input Section */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "28px",
            padding: isMobile ? "1.5rem" : "2rem",
            border: `1px solid ${getBorderColor()}`,
            marginBottom: "2rem",
          }}
        >
          <h3
            style={{
              color: getTextColor(),
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <motion.div
              animate={pulseAnimation}
              style={{
                width: "32px",
                height: "32px",
                background: "rgba(139,92,246,0.1)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaUserGraduate size={16} color="#8b5cf6" />
            </motion.div>
            Enter USNs to Compare
          </h3>

          {usns.map((usn, index) => (
            <motion.div
              key={`usn-input-${index}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}
            >
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={usn}
                  onChange={(e) => handleUsnChange(index, e.target.value)}
                  placeholder={`USN ${index + 1} (e.g., XXX00XX000)`}
                  style={{
                    width: "100%",
                    padding: "0.8rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    fontSize: "0.9rem",
                    outline: "none",
                    transition: "all 0.3s",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>
              {usns.length > 2 && (
                <motion.button
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleRemoveUsn(index)}
                  style={{
                    padding: "0 1rem",
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                  }}
                >
                  <FaTrash />
                </motion.button>
              )}
            </motion.div>
          ))}

          <motion.button
            whileHover={{ scale: 1.02, x: 5 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAddUsn}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              background: "none",
              border: `2px dashed #8b5cf6`,
              borderRadius: "12px",
              color: "#8b5cf6",
              cursor: "pointer",
              marginBottom: "1.5rem",
            }}
          >
            <FaPlus /> Add Another USN
          </motion.button>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextSecondary(),
                  fontWeight: "500",
                  fontSize: "0.85rem",
                }}
              >
                <FaCalendarAlt
                  style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                />{" "}
                Semester
              </label>
              <select
                value={semester}
                onChange={(e) => setSemester(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  outline: "none",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              >
                {semesters.map((sem) => (
                  <option key={`sem-${sem}`} value={sem}>
                    {sem === "all" ? "All Semesters" : `Semester ${sem}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextSecondary(),
                  fontWeight: "500",
                  fontSize: "0.85rem",
                }}
              >
                <FaUniversity
                  style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                />{" "}
                Exam Type
              </label>
              <select
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  outline: "none",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              >
                <option value="Regular">Regular</option>
                <option value="Revaluation">Revaluation</option>
              </select>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCompare}
            disabled={loading}
            style={{
              width: "100%",
              padding: "1rem",
              background: loading
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "16px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              boxShadow: loading ? "none" : "0 4px 14px 0 rgba(139,92,246,0.4)",
            }}
          >
            {loading ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <FaSpinner />
              </motion.div>
            ) : (
              <FaChartLine />
            )}
            {loading ? "Comparing..." : "Compare Results"}
          </motion.button>
        </motion.div>

        {/* Results Section */}
        <AnimatePresence mode="wait">
          {compared && results.length > 0 && (
            <motion.div
              id="comparison-results"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ duration: 0.4 }}
            >
              {/* Action Buttons */}
              <motion.div
                variants={itemVariants}
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  justifyContent: "flex-end",
                  marginBottom: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={downloadComparison}
                  style={{
                    padding: "0.4rem 1rem",
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  <FaDownload size={12} /> Download
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={shareComparison}
                  style={{
                    padding: "0.4rem 1rem",
                    background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  <FaShare size={12} /> Share
                </motion.button>
              </motion.div>

              {/* Header Card */}
              <motion.div
                variants={itemVariants}
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  borderRadius: "20px",
                  padding: "1rem 1.5rem",
                  marginBottom: "1.5rem",
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      color: "white",
                    }}
                  >
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <FaChartLine />
                    </motion.div>
                    Result Comparison
                  </h3>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      opacity: 0.9,
                      marginTop: "0.3rem",
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                    {examType} Results |{" "}
                    {semester === "all"
                      ? "All Semesters"
                      : `Semester ${semester}`}
                  </p>
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    opacity: 0.8,
                    color: "rgba(255,255,255,0.8)",
                  }}
                >
                  Compared on: {new Date().toLocaleString()}
                </div>
              </motion.div>

              {/* Leaderboard Style Table */}
              <motion.div
                variants={itemVariants}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  overflow: "hidden",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          background: isDarkMode
                            ? "rgba(139,92,246,0.05)"
                            : "#f8f9fa",
                          borderBottom: `2px solid ${getBorderColor()}`,
                        }}
                      >
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "center",
                            width: "70px",
                            color: getTextColor(),
                          }}
                        >
                          Rank
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "left",
                            color: getTextColor(),
                          }}
                        >
                          Student
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "center",
                            width: "100px",
                            color: getTextColor(),
                          }}
                        >
                          Total Marks
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "center",
                            width: "80px",
                            color: getTextColor(),
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "center",
                            width: "80px",
                            color: getTextColor(),
                          }}
                        >
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedResults.map((result, idx) => {
                        const rank = getRankBadge(idx);
                        const isExpanded = expandedStudent === result.usn;

                        return (
                          <React.Fragment key={result.usn}>
                            <motion.tr
                              custom={idx}
                              variants={tableRowVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                              style={{
                                borderBottom: `1px solid ${getBorderColor()}`,
                                background:
                                  idx === 0
                                    ? isDarkMode
                                      ? "rgba(139,92,246,0.1)"
                                      : "rgba(139,92,246,0.05)"
                                    : "transparent",
                              }}
                            >
                              <td
                                style={{ padding: "1rem", textAlign: "center" }}
                              >
                                <motion.div
                                  whileHover={{ scale: 1.2, rotate: 10 }}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "36px",
                                    height: "36px",
                                    borderRadius: "50%",
                                    background: rank.bg,
                                    color: rank.color,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {rank.icon || rank.label}
                                </motion.div>
                              </td>
                              <td style={{ padding: "1rem" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.8rem",
                                  }}
                                >
                                  <motion.img
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                      delay: idx * 0.05,
                                      type: "spring",
                                    }}
                                    src={`https://ui-avatars.com/api/?name=${result.name?.split(" ")[0] || "S"}&background=8b5cf6&color=fff&size=36&rounded=true`}
                                    alt={result.name}
                                    style={{
                                      width: "36px",
                                      height: "36px",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.9rem",
                                        color: getTextColor(),
                                      }}
                                    >
                                      {result.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "0.7rem",
                                        color: getTextSecondary(),
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {result.usn}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td
                                style={{ padding: "1rem", textAlign: "center" }}
                              >
                                <motion.div
                                  initial={{ scale: 0, rotate: -180 }}
                                  animate={{ scale: 1, rotate: 0 }}
                                  transition={{
                                    delay: idx * 0.05,
                                    type: "spring",
                                    stiffness: 400,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                      fontSize: "1.3rem",
                                      background:
                                        "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                    }}
                                  >
                                    {result.totalMarks}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.6rem",
                                      color: getTextSecondary(),
                                    }}
                                  >
                                    out of{" "}
                                    {result.subjects?.length * 100 || 600}
                                  </div>
                                </motion.div>
                              </td>
                              <td
                                style={{ padding: "1rem", textAlign: "center" }}
                              >
                                {result.resultStatus === "PASS" ? (
                                  <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                      delay: idx * 0.05,
                                      type: "spring",
                                    }}
                                    style={{
                                      background: "rgba(16,185,129,0.1)",
                                      color: "#10b981",
                                      padding: "0.25rem 0.75rem",
                                      borderRadius: "20px",
                                      fontSize: "0.75rem",
                                      fontWeight: "500",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.3rem",
                                    }}
                                  >
                                    <FaCheckCircle size={12} /> PASS
                                  </motion.span>
                                ) : (
                                  <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                      delay: idx * 0.05,
                                      type: "spring",
                                    }}
                                    style={{
                                      background: "rgba(239,68,68,0.1)",
                                      color: "#ef4444",
                                      padding: "0.25rem 0.75rem",
                                      borderRadius: "20px",
                                      fontSize: "0.75rem",
                                      fontWeight: "500",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.3rem",
                                    }}
                                  >
                                    <FaTimesCircle size={12} /> FAIL
                                  </motion.span>
                                )}
                              </td>
                              <td
                                style={{ padding: "1rem", textAlign: "center" }}
                              >
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => toggleExpand(result.usn)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#8b5cf6",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.3rem",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  <FaEye size={12} />{" "}
                                  {isExpanded ? "Hide" : "View"}
                                </motion.button>
                              </td>
                            </motion.tr>
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.tr
                                  variants={expandVariants}
                                  initial="hidden"
                                  animate="visible"
                                  exit="exit"
                                >
                                  <td colSpan="5" style={{ padding: "0" }}>
                                    <div
                                      style={{
                                        padding: "1rem 1.5rem",
                                        background: isDarkMode
                                          ? "rgba(139,92,246,0.05)"
                                          : "#fafafa",
                                      }}
                                    >
                                      <div>
                                        <motion.div
                                          initial={{ opacity: 0, x: -20 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            marginBottom: "1rem",
                                          }}
                                        >
                                          <motion.div
                                            animate={{ rotate: [0, 360] }}
                                            transition={{
                                              duration: 1,
                                              delay: 0.2,
                                            }}
                                          >
                                            <FaBook size={14} color="#8b5cf6" />
                                          </motion.div>
                                          <span
                                            style={{
                                              fontSize: "0.85rem",
                                              fontWeight: "600",
                                              color: getTextColor(),
                                            }}
                                          >
                                            Subject-wise Marks
                                          </span>
                                        </motion.div>
                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                              "repeat(auto-fill, minmax(220px, 1fr))",
                                            gap: "0.75rem",
                                          }}
                                        >
                                          {result.subjects?.map(
                                            (subject, i) => (
                                              <motion.div
                                                key={i}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.03 }}
                                                whileHover={{
                                                  scale: 1.02,
                                                  y: -2,
                                                }}
                                                style={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                  padding: "0.6rem 0.8rem",
                                                  background:
                                                    getCardBackground(),
                                                  borderRadius: "12px",
                                                  border: `1px solid ${getBorderColor()}`,
                                                }}
                                              >
                                                <div>
                                                  <span
                                                    style={{
                                                      fontFamily: "monospace",
                                                      fontSize: "0.7rem",
                                                      fontWeight: "600",
                                                      color: getTextColor(),
                                                    }}
                                                  >
                                                    {subject.subjectCode}
                                                  </span>
                                                  <span
                                                    style={{
                                                      fontSize: "0.6rem",
                                                      color: getTextSecondary(),
                                                      marginLeft: "0.3rem",
                                                    }}
                                                  >
                                                    {subject.subjectName?.substring(
                                                      0,
                                                      20,
                                                    )}
                                                  </span>
                                                </div>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.3rem",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      fontWeight: "bold",
                                                      fontSize: "0.85rem",
                                                      color:
                                                        subject.result === "P"
                                                          ? "#10b981"
                                                          : "#ef4444",
                                                    }}
                                                  >
                                                    {subject.total}
                                                  </span>
                                                  <span
                                                    style={{
                                                      fontSize: "0.6rem",
                                                      color:
                                                        subject.result === "P"
                                                          ? "#10b981"
                                                          : "#ef4444",
                                                    }}
                                                  >
                                                    ({subject.result})
                                                  </span>
                                                </div>
                                              </motion.div>
                                            ),
                                          )}
                                        </div>
                                        <motion.div
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: 0.2 }}
                                          style={{
                                            marginTop: "1rem",
                                            display: "flex",
                                            gap: "1rem",
                                            justifyContent: "flex-end",
                                            fontSize: "0.75rem",
                                            color: getTextSecondary(),
                                          }}
                                        >
                                          <span>
                                            <FaBook size={12} /> Passed:{" "}
                                            <strong
                                              style={{ color: "#10b981" }}
                                            >
                                              {result.passedSubjects || 0}
                                            </strong>
                                          </span>
                                          <span>
                                            <FaTimesCircle size={12} /> Failed:{" "}
                                            <strong
                                              style={{ color: "#ef4444" }}
                                            >
                                              {result.failedSubjects || 0}
                                            </strong>
                                          </span>
                                        </motion.div>
                                      </div>
                                    </div>
                                  </td>
                                </motion.tr>
                              )}
                            </AnimatePresence>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default CompareResults;
